import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Spiner from '../../components/Spiner'
import easyBuyLogic from '../../functions/logic/EasyBuyLogic'
import './EasyBuyTrial.scss'

const TABS = {
  OWN:         { key: 'own',         label: 'Mi propia tienda',      marketplace: false },
  MARKETPLACE: { key: 'marketplace', label: 'Unirme al marketplace', marketplace: true  },
}

const EMPTY_FORM = {
  tradeName:      '',
  code:           '',
  identification: '',
  ownerFirstName: '',
  ownerLastName:  '',
  ownerEmail:     '',
  phone:          '',
}

function Field({ label, id, error, hint, children }) {
  return (
    <div className={`eb-field${error ? ' eb-field--error' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && !error && <span className="eb-code-hint">{hint}</span>}
      {error && <span className="eb-field-error">{error}</span>}
    </div>
  )
}

export default function EasyBuyTrial() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [activeTab, setActiveTab]     = useState(TABS.OWN.key)
  const [form, setForm]               = useState(EMPTY_FORM)
  const [loading, setLoading]         = useState(false)
  const [success, setSuccess]         = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [globalError, setGlobalError] = useState('')

  const tab = Object.values(TABS).find((t) => t.key === activeTab)

  const setField = (name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.value }))
    if (fieldErrors[name] || fieldErrors['owner.' + name]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        delete next['owner.' + name]
        return next
      })
    }
    setGlobalError('')
  }

  const onlyNumbers = (name) => (e) => {
    if (/^\d*$/.test(e.target.value)) setField(name)(e)
  }

  const fieldError = (key) => fieldErrors[key] || ''

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFieldErrors({})
    setGlobalError('')
    setLoading(true)

    const response = await easyBuyLogic.registerTenantLogic(form, tab.marketplace)
    setLoading(false)

    if (!response.error) {
      setSuccess(true)
      return
    }

    if (response.status === 422 && response.fieldErrors) {
      setFieldErrors(response.fieldErrors)
      return
    }

    if (response.status === 409) {
      setGlobalError(response.message)
      return
    }

    enqueueSnackbar(response.message, {
      variant: response.variant || 'error',
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
    })
  }

  // ── Pantalla de éxito ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="eb-success-page">
        <div className="eb-success-card">
          <div className="eb-success-icon">
            <i className="bi bi-envelope-check-fill"></i>
          </div>
          <h2 className="eb-success-title">¡Listo! Revisa tu correo</h2>
          <p className="eb-success-desc">
            Enviamos un enlace a <strong>{form.ownerEmail}</strong> para que
            actives tu cuenta y elijas tu contraseña.
          </p>
          <p className="eb-success-hint">
            Si no ves el correo en unos minutos, revisa la carpeta de spam.
          </p>
          <button
            type="button"
            className="eb-success-btn"
            onClick={() => navigate('/')}
          >
            Ir al inicio
          </button>
        </div>
      </div>
    )
  }

  // ── Formulario ────────────────────────────────────────────────────────────────
  return (
    <>
      <Spiner opt={loading} loadLabel={global.LOAD_LABEL_SEND} />

      <section className="eb-trial-page">
        <div className="container">

          {/* Hero */}
          <div className="eb-hero">
            <div className="eb-hero-badge">
              <i className="bi bi-bag-check-fill"></i>
              Prueba gratuita · 30 días
            </div>
            <h1 className="eb-hero-title">
              Vende online con <span>EasyBuy</span>
            </h1>
            <p className="eb-hero-subtitle">
              Crea tu tienda, gestiona productos y pedidos desde una sola
              plataforma lista para crecer contigo. Sin tarjeta de crédito.
            </p>
          </div>

          {/* Franja de microbeneficios */}
          <div className="eb-features-strip">
            <div className="eb-feature-item">
              <i className="bi bi-shop"></i>Tienda lista en minutos
            </div>
            <div className="eb-feature-item">
              <i className="bi bi-box-seam"></i>Productos ilimitados
            </div>
            <div className="eb-feature-item">
              <i className="bi bi-credit-card"></i>Pagos online
            </div>
            <div className="eb-feature-item">
              <i className="bi bi-bar-chart-line"></i>Reportes de ventas
            </div>
            <div className="eb-feature-item">
              <i className="bi bi-headset"></i>Soporte incluido
            </div>
          </div>

          {/* Tabs */}
          <div className="eb-tabs-wrapper">
            <div className="eb-tabs" role="tablist">
              {Object.values(TABS).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.key}
                  className={`eb-tab${activeTab === t.key ? ' eb-tab--active' : ''}`}
                  onClick={() => {
                    setActiveTab(t.key)
                    setFieldErrors({})
                    setGlobalError('')
                  }}
                >
                  <i className={t.key === 'own' ? 'bi bi-shop' : 'bi bi-grid-3x3-gap'}></i>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nota marketplace */}
          {activeTab === TABS.MARKETPLACE.key && (
            <div className="eb-marketplace-note">
              <i className="bi bi-info-circle-fill"></i>
              Tu solicitud pasará por aprobación manual del equipo de EasyBuy
              antes de activarse en el marketplace.
            </div>
          )}

          {/* Error global — duplicado (409) */}
          {globalError && (
            <div className="eb-global-error">
              <i className="bi bi-exclamation-triangle-fill"></i>
              {globalError}
            </div>
          )}

          {/* Card principal */}
          <div className="eb-card-layout">

            {/* Panel izquierdo */}
            <div className="eb-card-left">
              <div className="eb-left-badge">
                <i className="bi bi-lightning-charge-fill"></i>EasyBuy
              </div>

              <h2 className="eb-left-headline">
                30 días para <span>probar todo</span>
              </h2>

              <p className="eb-left-desc">
                Sin compromisos. Solo ingresa tus datos y empieza a vender
                hoy mismo.
              </p>

              <div className="eb-left-benefits">
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Tienda online lista en minutos
                </div>
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Catálogo de productos ilimitado
                </div>
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Gestión de pedidos y clientes
                </div>
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Pagos online y contra entrega
                </div>
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Reportes de ventas en tiempo real
                </div>
                <div className="eb-left-benefit">
                  <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                  Soporte técnico incluido
                </div>
                {activeTab === TABS.MARKETPLACE.key && (
                  <div className="eb-left-benefit">
                    <div className="eb-benefit-icon"><i className="bi bi-check-lg"></i></div>
                    Acceso al marketplace de EasyBuy
                  </div>
                )}
              </div>

              <div className="eb-left-trial-box">
                <div className="eb-trial-days">30</div>
                <div className="eb-trial-label">
                  <strong>Días gratis</strong>
                  Sin tarjeta de crédito
                </div>
              </div>
            </div>

            {/* Panel derecho — formulario */}
            <div className="eb-card-right">
              <form noValidate onSubmit={handleSubmit}>

                <p className="eb-form-section-title">Tu tienda</p>
                <div className="eb-form-divider"></div>

                <div className="row g-3">
                  <div className="col-md-8">
                    <Field
                      label="Nombre de tu tienda"
                      id="eb-tradename"
                      error={fieldError('trade_name')}
                    >
                      <input
                        id="eb-tradename"
                        type="text"
                        className="eb-input"
                        value={form.tradeName}
                        onChange={setField('tradeName')}
                        placeholder="Mi Tienda Online"
                        maxLength={120}
                        autoComplete="organization"
                      />
                    </Field>
                  </div>

                  <div className="col-md-4">
                    <Field
                      label={<>Subdominio <span className="eb-optional">(opcional)</span></>}
                      id="eb-code"
                      error={fieldError('code')}
                      hint="Se genera automáticamente si lo dejas vacío."
                    >
                      <input
                        id="eb-code"
                        type="text"
                        className="eb-input"
                        value={form.code}
                        onChange={(e) => {
                          const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                          setForm((p) => ({ ...p, code: v }))
                          setFieldErrors((p) => { const n = { ...p }; delete n.code; return n })
                        }}
                        placeholder="mi-tienda"
                        maxLength={50}
                      />
                    </Field>
                  </div>

                  <div className="col-md-6">
                    <Field
                      label="RUC o cédula"
                      id="eb-identification"
                      error={fieldError('identification')}
                    >
                      <input
                        id="eb-identification"
                        type="text"
                        inputMode="numeric"
                        className="eb-input"
                        value={form.identification}
                        onChange={onlyNumbers('identification')}
                        placeholder="10 dígitos (cédula) o 13 (RUC)"
                        maxLength={13}
                      />
                    </Field>
                  </div>

                  <div className="col-md-6">
                    <Field
                      label={<>Teléfono <span className="eb-optional">(opcional)</span></>}
                      id="eb-phone"
                      error={fieldError('phone')}
                    >
                      <input
                        id="eb-phone"
                        type="tel"
                        inputMode="numeric"
                        className="eb-input"
                        value={form.phone}
                        onChange={onlyNumbers('phone')}
                        placeholder="0991234567"
                        maxLength={15}
                        autoComplete="tel"
                      />
                    </Field>
                  </div>
                </div>

                <p className="eb-form-section-title" style={{ marginTop: 24 }}>Tus datos</p>
                <div className="eb-form-divider"></div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <Field
                      label="Tu nombre"
                      id="eb-firstname"
                      error={fieldError('owner.first_name')}
                    >
                      <input
                        id="eb-firstname"
                        type="text"
                        className="eb-input"
                        value={form.ownerFirstName}
                        onChange={setField('ownerFirstName')}
                        placeholder="Nombre"
                        maxLength={80}
                        autoComplete="given-name"
                      />
                    </Field>
                  </div>

                  <div className="col-md-6">
                    <Field
                      label={<>Apellido <span className="eb-optional">(opcional)</span></>}
                      id="eb-lastname"
                      error={fieldError('owner.last_name')}
                    >
                      <input
                        id="eb-lastname"
                        type="text"
                        className="eb-input"
                        value={form.ownerLastName}
                        onChange={setField('ownerLastName')}
                        placeholder="Apellido"
                        maxLength={80}
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <div className="col-12">
                    <Field
                      label="Tu correo electrónico"
                      id="eb-email"
                      error={fieldError('owner.email')}
                    >
                      <input
                        id="eb-email"
                        type="email"
                        className="eb-input"
                        value={form.ownerEmail}
                        onChange={setField('ownerEmail')}
                        placeholder="tu@correo.com"
                        maxLength={150}
                        autoComplete="email"
                      />
                    </Field>
                  </div>
                </div>

                <div className="eb-actions">
                  <button
                    type="button"
                    className="eb-btn-back"
                    onClick={() => navigate('/')}
                  >
                    Regresar
                  </button>
                  <button
                    type="submit"
                    className="eb-btn-submit"
                    disabled={loading}
                  >
                    Solicitar prueba gratuita
                  </button>
                </div>

              </form>
            </div>

          </div>
        </div>
      </section>
    </>
  )
}
